/**
 * Formular-Analyse Seite
 * - Vollständige Ansicht des Formular-Assistenten
 * - Dokument-Auswahl oder direkter Upload
 */

import { useState, useEffect, useCallback } from 'react';
import {
  FileSearch, Upload, FileText, Users, ChevronRight,
  Inbox, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import FormularAssistent from '@/components/FormularAssistent';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function FormularAnalysePage() {
  const [klienten, setKlienten] = useState([]);
  const [selectedKlient, setSelectedKlient] = useState('');
  const [dokumente, setDokumente] = useState([]);
  const [selectedDokument, setSelectedDokument] = useState(null);
  const [ocrText, setOcrText] = useState('');
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: Auswahl, 2: Analyse

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const kl = await betreuungAPI.listKlienten();
      setKlienten(kl);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Lade Dokumente wenn Klient gewählt
  useEffect(() => {
    if (selectedKlient) {
      loadDokumente();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKlient]);

  const loadDokumente = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/betreuung/dokumente?klient_id=${selectedKlient}`);
      const data = await res.json();
      // Nur Dokumente mit OCR-Text
      const mitOcr = data.filter(d => d.ocr_text || d.dokumententyp === 'posteingang');
      setDokumente(mitOcr);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDokumentSelect = async (dokId) => {
    const dok = dokumente.find(d => d.id === dokId);
    if (dok) {
      setSelectedDokument(dok);
      setOcrText(dok.ocr_text || '');
      
      if (!dok.ocr_text) {
        toast.info('Dieses Dokument hat noch keinen OCR-Text. Bitte zuerst im Posteingang verarbeiten.');
      }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    toast.info('OCR wird durchgeführt...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${BACKEND_URL}/api/ocr/extract`, {
        method: 'POST',
        body: formData
      });
      
      const data = await res.json();
      
      if (data.text) {
        setOcrText(data.text);
        setSelectedDokument({ titel: file.name, id: null });
        toast.success('OCR erfolgreich');
      } else {
        toast.error('OCR fehlgeschlagen');
      }
    } catch (err) {
      console.error(err);
      toast.error('Upload fehlgeschlagen');
    }
  };

  const startAnalyse = () => {
    if (!selectedKlient) {
      toast.error('Bitte wählen Sie einen Klienten aus');
      return;
    }
    if (!ocrText && !selectedDokument) {
      toast.error('Bitte wählen Sie ein Dokument aus oder laden Sie eines hoch');
      return;
    }
    setStep(2);
  };

  return (
    <div className="space-y-6" data-testid="formular-analyse-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileSearch className="w-6 h-6" /> Formular-Analyse
          </h1>
          <p className="text-slate-500 mt-1">
            ZEO analysiert Antragsformulare und füllt sie automatisch aus
          </p>
        </div>
        {step === 2 && (
          <Button variant="outline" onClick={() => setStep(1)}>
            Zurück zur Auswahl
          </Button>
        )}
      </div>

      {/* Schritt 1: Auswahl */}
      {step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Linke Seite: Klient & Dokument auswählen */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-5 h-5" />
                1. Klient auswählen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={selectedKlient || 'none'} onValueChange={v => setSelectedKlient(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="select-klient">
                  <SelectValue placeholder="Betreuten auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Bitte wählen --</SelectItem>
                  {klienten.map(k => (
                    <SelectItem key={k.id} value={k.id}>{k.vorname} {k.nachname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-5 h-5" />
                2. Formular auswählen
              </CardTitle>
              <CardDescription>
                Wählen Sie ein bestehendes Dokument oder laden Sie ein neues hoch
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Dokument aus Akte */}
              {selectedKlient && dokumente.length > 0 && (
                <div>
                  <Label className="text-sm text-slate-500">Aus der Akte:</Label>
                  <Select 
                    value={selectedDokument?.id || 'none'} 
                    onValueChange={v => v !== 'none' && handleDokumentSelect(v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Dokument auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Dokument wählen --</SelectItem>
                      {dokumente.map(d => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.titel || 'Unbenannt'} {d.ocr_text ? '✓' : '(kein OCR)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Oder: Hochladen */}
              <div>
                <Label className="text-sm text-slate-500">Oder neu hochladen:</Label>
                <div className="mt-1">
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                    <Upload className="w-5 h-5 text-slate-400" />
                    <span className="text-slate-600">PDF oder Bild hochladen</span>
                    <input 
                      type="file" 
                      accept=".pdf,.png,.jpg,.jpeg" 
                      className="hidden" 
                      onChange={handleFileUpload}
                    />
                  </label>
                </div>
              </div>

              {/* Ausgewähltes Dokument */}
              {selectedDokument && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm font-medium text-green-800">
                    ✓ Ausgewählt: {selectedDokument.titel}
                  </p>
                  {ocrText && (
                    <p className="text-xs text-green-600 mt-1">
                      {ocrText.length} Zeichen OCR-Text vorhanden
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Start-Button */}
          <div className="lg:col-span-2">
            <Button 
              onClick={startAnalyse} 
              disabled={!selectedKlient || (!ocrText && !selectedDokument)}
              className="w-full sm:w-auto"
              size="lg"
            >
              <FileSearch className="w-5 h-5 mr-2" />
              Analyse starten
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Schritt 2: Analyse */}
      {step === 2 && (
        <FormularAssistent 
          dokumentId={selectedDokument?.id}
          ocrText={ocrText}
          klientId={selectedKlient}
        />
      )}

      {/* Info-Box */}
      {step === 1 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <FileSearch className="w-6 h-6 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">So funktioniert die Formular-Analyse:</p>
                <ol className="mt-2 space-y-1 text-sm text-blue-700">
                  <li>1. ZEO erkennt den Formulartyp (Rentenantrag, Grundsicherung, Wohngeld, etc.)</li>
                  <li>2. Felder werden automatisch aus der Klientenakte befüllt</li>
                  <li>3. Fehlende Daten werden markiert mit Vorschlägen zur Beschaffung</li>
                  <li>4. Passende Anlagen werden aus der Akte vorgeschlagen</li>
                  <li>5. Sie erhalten ein fertig ausgefülltes PDF zum Ausdrucken</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
